import type { KeyboardEvent, MouseEvent } from 'react';

export const TABLE_MENU_ITEM_BUTTON_CLASS =
  'block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer transition-colors duration-200 focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-gray-500';

/** Fixed + portal: evita corte por overflow do BsdsCard / overflow-x-auto da tabela. */
export const TABLE_MENU_DROPDOWN_CLASS =
  'fixed bg-white rounded-md shadow-lg py-1 z-[100] border border-gray-200';

export const TABLE_MENU_TRIGGER_BUTTON_CLASS =
  'hover:bg-gray-100 focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-gray-500 rounded transition-colors p-1';

export type TableMenuAlign = 'left' | 'right';

type TableMenuPositionOptions = {
  align?: TableMenuAlign;
  menuWidth?: number;
  menuHeight?: number;
  offset?: number;
};

export function getTableMenuFixedPosition(
  trigger: HTMLElement,
  {
    align = 'left',
    menuWidth = 160,
    menuHeight = 160,
    offset = 4,
  }: TableMenuPositionOptions = {},
): { top: number; left: number } {
  const rect = trigger.getBoundingClientRect();
  const viewportPadding = 8;

  let left = align === 'right' ? rect.right - menuWidth : rect.left;
  left = Math.max(
    viewportPadding,
    Math.min(left, window.innerWidth - menuWidth - viewportPadding),
  );

  const spaceBelow = window.innerHeight - rect.bottom - offset;
  const openUpward = spaceBelow < menuHeight && rect.top > spaceBelow;
  const top = openUpward
    ? Math.max(viewportPadding, rect.top - menuHeight - offset)
    : rect.bottom + offset;

  return { top, left };
}

export function registerMapRef<TId, TElement extends HTMLElement>(
  map: Map<TId, TElement>,
  id: TId,
  element: TElement | null,
): void {
  if (element) {
    map.set(id, element);
    return;
  }
  map.delete(id);
}

type MenuItemActionHandlersParams<T> = {
  onAction: (item: T) => void;
  closeMenu: () => void;
  focusMenuButton: (item: T) => void;
  canExecute?: (item: T) => boolean;
};

export function createMenuItemActionHandlers<T>({
  onAction,
  closeMenu,
  focusMenuButton,
  canExecute = () => true,
}: MenuItemActionHandlersParams<T>) {
  return {
    handleClick: (item: T, event: MouseEvent) => {
      event.stopPropagation();
      if (!canExecute(item)) {
        return;
      }
      onAction(item);
      closeMenu();
    },
    handleKeyDown: (item: T, event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        event.stopPropagation();
        if (canExecute(item)) {
          onAction(item);
          closeMenu();
        }
      } else if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        closeMenu();
        focusMenuButton(item);
      }
    },
  };
}
